import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Modal, message } from "antd";
import { trackRegisterClick, trackInfoClick } from "services/statsService";
import "./SingleHomepage.css";

const SingleHomepage = ({ siteConfig, categories, registrationsByCategory }) => {
  const history = useHistory();
  const [infoModal, setInfoModal] = useState({ visible: false, url: "", title: "" });
  const [registerPickerVisible, setRegisterPickerVisible] = useState(false);

  const toAbsoluteUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    const base = process.env.REACT_APP_API?.replace("/api/", "") || "";
    return `${base}/${url}`;
  };

  const heroImageUrl = toAbsoluteUrl(siteConfig?.homepage_hero_image);
  const homepageText = siteConfig?.homepage_text || "";

  // Flatten all registrations across categories, preserving category reference
  // Exclude hidden registrations (status=2)
  const isHidden = (reg) => reg.status === "2" || reg.status === 2;
  const allRegistrations = [];
  if (categories && categories.length > 0 && registrationsByCategory) {
    categories.forEach((cat) => {
      const regs = registrationsByCategory[String(cat.id)] || [];
      regs.forEach((reg) => {
        if (!isHidden(reg)) allRegistrations.push({ ...reg, _category: cat });
      });
    });
  } else if (registrationsByCategory) {
    // Single-homepage fallback: allow registrations to render without configured categories
    Object.keys(registrationsByCategory).forEach((groupKey) => {
      const regs = registrationsByCategory[groupKey] || [];
      regs.forEach((reg) => {
        if (!isHidden(reg)) allRegistrations.push({ ...reg, _category: null });
      });
    });
  }

  const handleRegister = (registrationId) => {
    trackRegisterClick(registrationId);
    localStorage.setItem("camper_step", "0");
    history.push(`/register?id=${registrationId}`);
  };

  const handleInfo = (reg) => {
    trackInfoClick(reg.id, reg._category?.id);
    const cat = reg._category;
    // If the category has an info page, navigate there
    if (cat?.info_page_enabled === "1" || cat?.info_page_enabled === 1) {
      history.push(`/info/${cat.id}`);
      return;
    }
    // Otherwise open the registration's PDF/info URL in a modal
    if (reg.homepage_info) {
      setInfoModal({ visible: true, url: reg.homepage_info, title: reg.registration_name });
    }
  };

  const hasInfo = (reg) => {
    const cat = reg._category;
    return (
      cat?.info_page_enabled === "1" ||
      cat?.info_page_enabled === 1 ||
      !!reg.homepage_info
    );
  };

  const isOpenRegistration = (reg) => {
    const isOpen = reg.status === "1" || reg.status === 1;
    const noRegRequired =
      reg.no_registration_required === "1" ||
      reg.no_registration_required === 1;
    return isOpen && !noRegRequired;
  };

  const openRegistrations = allRegistrations.filter(isOpenRegistration);
  // Show INFO on homepage only when there's exactly one OPEN registration
  const singleOpenRegistration = openRegistrations.length === 1 ? openRegistrations[0] : null;
  const firstInfoRegistration = singleOpenRegistration && hasInfo(singleOpenRegistration)
    ? singleOpenRegistration
    : null;

  const handleMainRegisterClick = () => {
    if (allRegistrations.length > 1) {
      setRegisterPickerVisible(true);
      return;
    }

    if (openRegistrations.length === 0) {
      message.info("There are no current open registrations");
      return;
    }

    handleRegister(openRegistrations[0].id);
  };

  return (
    <div className="single-homepage">
      <div className="single-hero">
        {/* Left: text + CTA */}
        <div className="single-hero-content">
          <div className="single-hero-copy">
            {homepageText ? (
              <div
                className="single-hero-text"
                dangerouslySetInnerHTML={{ __html: homepageText }}
              />
            ) : (
              <div className="single-hero-text">
                <h2>{siteConfig?.site_title}</h2>
                {siteConfig?.site_subtitle && <p>{siteConfig.site_subtitle}</p>}
              </div>
            )}
          </div>

          {allRegistrations.length > 0 && (
            <div className="single-hero-cta">
              <div className="single-cta-row">
                <button
                  className={`single-register-btn${openRegistrations.length === 0 ? " closed" : ""}`}
                  onClick={handleMainRegisterClick}
                >
                  REGISTER
                </button>
                {firstInfoRegistration && (
                  <span
                    className="single-info-link"
                    onClick={() => handleInfo(firstInfoRegistration)}
                  >
                    INFO
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right: hero image */}
        <div className="single-hero-image">
          {heroImageUrl ? (
            <img src={heroImageUrl} alt="hero" />
          ) : (
            <div className="single-hero-image-placeholder">&#x1F3DE;</div>
          )}
        </div>
      </div>

      {/* Info PDF modal */}
      <Modal
        visible={infoModal.visible}
        title={infoModal.title}
        onCancel={() => setInfoModal({ visible: false, url: "", title: "" })}
        footer={null}
        width="80vw"
        bodyStyle={{ padding: 0, height: "75vh" }}
      >
        <iframe
          src={infoModal.url}
          title={infoModal.title}
          style={{ width: "100%", height: "100%", border: "none" }}
        />
      </Modal>

      {/* Register picker modal for multiple open registrations */}
      <Modal
        visible={registerPickerVisible}
        title="Select Registration"
        onCancel={() => setRegisterPickerVisible(false)}
        footer={null}
        width={560}
      >
        <div className="single-register-picker-list">
          {openRegistrations.map((reg) => (
            <div key={reg.id} className="single-register-picker-row">
              <button
                className="single-register-picker-item"
                onClick={() => {
                  setRegisterPickerVisible(false);
                  handleRegister(reg.id);
                }}
              >
                {reg.registration_name}
              </button>
              {hasInfo(reg) && (
                <span
                  className="single-picker-info-link"
                  onClick={() => {
                    setRegisterPickerVisible(false);
                    handleInfo(reg);
                  }}
                >
                  INFO
                </span>
              )}
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default SingleHomepage;
